import React, { useState } from 'react';
import { Upload, Button, message, Form, Space, Typography, Table, Modal, Tag } from 'antd';
import { UploadOutlined, InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { workflowAPI } from '../services/api';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

const WorkflowBatchUploader = ({ onUploadSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadResults, setUploadResults] = useState(null);
  const [resultsModalVisible, setResultsModalVisible] = useState(false);
  
  // 文件上传前检查
  const beforeUpload = (file) => {
    const isJSON = file.type === 'application/json' || file.name.endsWith('.json');
    if (!isJSON) {
      message.error('只能上传JSON文件!');
      return Upload.LIST_IGNORE;
    }
    
    // 检查文件名是否符合数字.json格式
    const filenameMatch = file.name.match(/^(\d+)\.json$/i);
    if (!filenameMatch) {
      message.warning(`文件 ${file.name} 不符合"数字.json"格式，上传后将使用随机编号`);
    }
    
    return true;
  };

  const handleChange = ({ fileList }) => {
    setFileList(fileList);
  };
  
  // 移除文件
  const handleRemove = (file) => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);
    setFileList(newFileList);
  };

  // 执行批量上传
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('请选择至少一个工作流文件');
      return;
    }

    try {
      setLoading(true);
      
      const formData = new FormData();
      fileList.forEach(file => {
        formData.append('workflow_files', file.originFileObj);
      });
      
      const response = await workflowAPI.batchUploadWorkflows(formData);
      
      setUploadResults(response.data);
      setResultsModalVisible(true);
      setFileList([]);
      
      message.success(response.message || '批量上传工作流完成');
      
      // 通知父组件上传成功
      if (onUploadSuccess && response.data.successful.length > 0) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error('批量上传工作流失败:', error);
      message.error('批量上传工作流失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 结果表格列定义
  const successColumns = [
    {
      title: '工作流编号',
      dataIndex: 'workflow_num',
      key: 'workflow_num',
    },
    {
      title: '工作流名称',
      dataIndex: 'workflow_name',
      key: 'workflow_name',
    },
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
    }
  ];
  
  const errorColumns = [
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
    },
    {
      title: '错误原因',
      dataIndex: 'error',
      key: 'error',
    }
  ];

  return (
    <>
      <Paragraph>
        <Text type="secondary">
          <InfoCircleOutlined /> 拖入或选择多个JSON文件进行批量上传。文件名格式为"数字.json"(如11.json)时，将使用文件名中的数字作为工作流编号。
        </Text>
      </Paragraph>
      
      <Dragger
        multiple={true}
        fileList={fileList}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        onRemove={handleRemove}
        accept=".json"
        customRequest={({ onSuccess }) => {
          setTimeout(() => {
            onSuccess("ok");
          }, 0);
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
        <p className="ant-upload-hint">
          支持单个或批量上传，文件将自动识别文件名作为工作流编号
        </p>
      </Dragger>
      
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Button
          type="primary"
          onClick={handleUpload}
          disabled={fileList.length === 0}
          loading={loading}
          icon={<UploadOutlined />}
        >
          {loading ? '上传中' : '开始上传'}
        </Button>
      </div>
      
      {/* 上传结果弹窗 */}
      <Modal
        title="批量上传结果"
        open={resultsModalVisible}
        onCancel={() => setResultsModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setResultsModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {uploadResults && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Tag color="green">成功: {uploadResults.successful.length}</Tag>
                <Tag color="red">失败: {uploadResults.failed.length}</Tag>
              </Space>
            </div>
            
            {uploadResults.successful.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <h4>上传成功:</h4>
                <Table
                  dataSource={uploadResults.successful}
                  columns={successColumns}
                  rowKey="workflow_num"
                  size="small"
                  pagination={false}
                />
              </div>
            )}
            
            {uploadResults.failed.length > 0 && (
              <>
                <h4>上传失败:</h4>
                <Table
                  dataSource={uploadResults.failed}
                  columns={errorColumns}
                  rowKey="filename"
                  size="small"
                  pagination={false}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default WorkflowBatchUploader; 